import { styled } from '@mui/material';
import type { ReactNode } from 'react';

import { Card } from '../Card';

type Props = {
  /** Navigation pinned at the top of the panel, typically `Tabs`. */
  header?: ReactNode;
  /** Controls acting on the list (search, filters), pinned under the header. */
  toolbar?: ReactNode;
  children: ReactNode;
  /** Controls pinned under the list, typically a `TablePagination`. */
  footer?: ReactNode;
  /**
   * Fills the remaining height of a fixed-header page from the `sm` breakpoint and scrolls the
   * content inside the panel. On phones the page scrolls naturally.
   */
  fill?: boolean;
  'data-testid'?: string;
};

type OwnerState = {
  fill: boolean;
};

const Root = styled(Card)<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  ...(ownerState.fill && {
    [theme.breakpoints.up('sm')]: {
      flex: 1,
      minHeight: 0,
    },
  }),
}));

const Header = styled('div')({
  flexShrink: 0,
});

const Toolbar = styled('div')(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const Footer = styled('div')(({ theme }) => ({
  flexShrink: 0,
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const Body = styled('div')<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  ...(ownerState.fill && {
    [theme.breakpoints.up('sm')]: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      overscrollBehavior: 'contain',
    },
  }),
}));

/**
 * Outlined panel hosting a list with its navigation (tabs) and toolbar (search, filters).
 * Use it for inbox-like screens where the list is the page's main working surface.
 */
export function ListPanel(props: Props) {
  const ownerState = { fill: Boolean(props.fill) };

  return (
    <Root ownerState={ownerState} data-testid={props['data-testid'] ?? 'list-panel'}>
      {props.header ? <Header>{props.header}</Header> : null}
      {props.toolbar ? <Toolbar>{props.toolbar}</Toolbar> : null}
      <Body ownerState={ownerState}>{props.children}</Body>
      {props.footer ? <Footer>{props.footer}</Footer> : null}
    </Root>
  );
}

export type { Props as ListPanelProps };
